<template>
  <div class="c-contact-selector h-full flex" :class="{ 'flex-col': singleColumnView }">
    <div class="flex-1 h-full overflow-hidden">
      <CSearchOnClick
        :disabled="!!loading"
        full-width
        :initial-search-value="search"
        :placeholder="`${t('canvasUI.CContactSelector.search')}...`"
        @search="emit('update:search', $event)"
      />

      <CQuickFilters
        v-if="showAvailableFilters.length > 1"
        :available-filters="showAvailableFilters"
        class="mt-4"
        :model-value="selectedQuickFilters"
        :mutually-exclusive="mutuallyExclusiveQuickFilters"
        :title="quickFiltersTitle"
        @update:model-value="emit('update:selectedQuickFilters', $event)"
      />

      <!-- TODO: change render condition if we don't want to hide orphan contacts -->
      <CContactSelectorAccountsWithContacts
        v-if="accounts?.length && showCustomers"
        :key="contactSelectorKey"
        :accounts="accounts"
        :allow-account-change="allowAccountChange"
        :allow-contacts-from-different-accounts="allowContactsFromDifferentAccounts"
        class="mt-6 h-full"
        :contacts="contacts"
        :custom-account-display-field="customAccountDisplayField"
        :date-format="dateFormat"
        :get-account-second-line="getAccountSecondLine"
        :loading="loading"
        :mutually-exclusive-quick-filters="mutuallyExclusiveQuickFilters"
        :restrict-to-single-contact="restrictToSingleContact"
        :search="search"
        :selected-account-ids="selectedAccountIds"
        :selected-contact-ids="selectedContactIds"
        :single-column-view="singleColumnView"
        :total-accounts-count="totalAccounts"
        @add-account="onAdd('selectedAccountIds', $event)"
        @add-contact="onAddContact"
        @remove-account="onRemove('selectedAccountIds', $event)"
        @remove-contact="onRemove('selectedContactIds', $event)"
      />

      <CContactSelectorColleagues
        v-if="colleagues?.length && showColleagues"
        class="mt-6"
        :colleagues="colleagues"
        :loading="loading"
        :mutually-exclusive-quick-filters="mutuallyExclusiveQuickFilters"
        :search="search"
        :selected-colleague-ids="selectedColleagueIds"
        :total-colleagues="totalColleagues"
        @add-colleague="onAdd('selectedColleagueIds', $event)"
        @remove-colleague="onRemove('selectedColleagueIds', $event)"
      />
      <div v-if="!accounts?.length && !colleagues?.length && (showColleagues || showCustomers)" class="mt-5">
        <NH6 v-if="!loading">Nothing found</NH6>
        <NH6 v-else>Loading</NH6>
      </div>
    </div>
    <div
      :class="{
        'contact-selector__selected-wrapper pl-3': !singleColumnView,
        'single-column-selected': singleColumnView,
      }"
    >
      <CContactSelectorSelected
        :accounts="accounts"
        :allow-account-change="allowAccountChange"
        :colleagues="colleagues"
        :contacts="contacts"
        :is-draggable="isDraggable"
        :loading="loading"
        :selected-account-ids="selectedAccountIds"
        :selected-colleague-ids="selectedColleagueIds"
        :selected-contact-ids="selectedContactIds"
        :simpleView="singleColumnView"
        @remove-account="onRemove('selectedAccountIds', $event)"
        @remove-all="onRemoveAll"
        @remove-colleague="onRemove('selectedColleagueIds', $event)"
        @remove-contact="onRemove('selectedContactIds', $event)"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { NH6 } from 'naive-ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import useThemeVars from '@lib/composables/useThemeVars'
import CSearchOnClick from '@lib/components/CSearch/CSearchOnClick.vue'
import CContactSelectorSelected from '@lib/components/CContactSelector/CContactSelectorSelected.vue'
import CContactSelectorAccountsWithContacts from '@lib/components/CContactSelector/CContactSelectorAccountsWithContacts.vue'
import CContactSelectorColleagues from '@lib/components/CContactSelector/CContactSelectorColleagues.vue'
import CQuickFilters from '@lib/components/CQuickFilters/CQuickFilters.vue'
import { ContactSelectorQuickFilterType } from '@lib/components/CContactSelector/CContactSelector.types'

const { t } = useI18n()
const themeVars = useThemeVars()

const emit = defineEmits([
  'update:search',
  'update:selectedQuickFilters',
  'update:selectedAccountIds',
  'update:selectedContactIds',
  'update:selectedColleagueIds',
])
const props = withDefaults(
  defineProps<{
    totalAccounts: number
    totalContacts: number
    totalColleagues: number
    accounts?: any[]
    contacts?: any[]
    colleagues?: any[]
    loading?: boolean
    singleColumnView?: boolean
    allowAccountChange?: boolean
    allowContactsFromDifferentAccounts?: boolean
    restrictToSingleContact?: boolean
    isDraggable?: boolean
    quickFiltersTitle?: string
    mutuallyExclusiveQuickFilters?: boolean
    customAccountDisplayField?: string
    getAccountSecondLine?: (account: any) => string
    dateFormat?: string
    // selections
    search: string
    selectedQuickFilters?: string[]
    selectedColleagueIds?: string[]
    selectedContactIds?: string[]
    selectedAccountIds?: string[]
  }>(),
  {
    colleagues: () => [],
    contacts: () => [],
    accounts: () => [],
    quickFilters: () => [],
    loading: false,
    singleColumnView: false,
    allowAccountChange: true,
    restrictToSingleContact: false,
    isDraggable: false,
    mutuallyExclusiveQuickFilters: false,
    dateFormat: 'DD/MM/YYYY',
    // selections
    selectedQuickFilters: () => [],
    selectedColleagueIds: () => [],
    selectedContactIds: () => [],
    selectedAccountIds: () => [],
    search: '',
  },
)

const showAvailableFilters = computed(() => {
  if (props.totalColleagues > 0) {
    return [
      {
        name: ContactSelectorQuickFilterType.CUSTOMERS,
        displayName: t('canvasUI.CContactSelector.quickFilters.customers'),
      },
      {
        name: ContactSelectorQuickFilterType.COLLEAGUES,
        displayName: t('canvasUI.CContactSelector.quickFilters.colleagues'),
      },
    ]
  }
  return [
    {
      name: ContactSelectorQuickFilterType.CUSTOMERS,
      displayName: t('canvasUI.CContactSelector.quickFilters.customers'),
    },
  ]
})

const showCustomers = computed(
  () =>
    !props.selectedQuickFilters.length || props.selectedQuickFilters.includes(ContactSelectorQuickFilterType.CUSTOMERS),
)
const showColleagues = computed(
  () =>
    !props.selectedQuickFilters.length ||
    props.selectedQuickFilters.includes(ContactSelectorQuickFilterType.COLLEAGUES),
)

const contactSelectorKey = computed(() => {
  // Create shorter, more efficient keys
  const searchHash = props.search ? btoa(encodeURIComponent(props.search)).slice(0, 50) : 'no-search'

  // Create a simple hash of the actual data objects
  const dataObject = {
    accounts: props.accounts.map((a) => a.Id),
    contacts: props.contacts.map((c) => c.Id),
  }
  const dataHash = btoa(JSON.stringify(dataObject)).slice(0, 50)

  return `${searchHash}-${dataHash}`
})

type Key = 'selectedAccountIds' | 'selectedContactIds' | 'selectedColleagueIds'

const onAdd = (key: Key, addedId: string) => {
  emit(`update:${key}`, [...(props[key] as any).filter((id: string | number) => id !== addedId), addedId])
}

const onAddContact = (contactId: string) => {
  if (props.restrictToSingleContact) {
    emit('update:selectedContactIds', [contactId])
  } else {
    onAdd('selectedContactIds', contactId)
  }
}

const onRemove = (key: Key, removedId: string) => {
  emit(
    `update:${key}`,
    (props[key] as any).filter((id: string | number) => id !== removedId),
  )
}

const onRemoveAll = () => {
  emit('update:selectedAccountIds', [])
  emit('update:selectedContactIds', [])
  emit('update:selectedColleagueIds', [])
}
</script>

<style lang="scss" scoped>
.c-contact-selector {
  min-height: 450px;
}

.contact-selector__selected-wrapper {
  max-width: 292px;
  width: 100%;
}

.single-column-selected {
  bottom: 0;
  background: v-bind('themeVars.base');
}
</style>
